<!-- Project field of a Vue dialog (video, hook, creator, source): one pill per project, shown only with at least two
     projects (otherwise the block stays in the page, hidden, like the legacy #projPickWrap_<kind>, so the spacing of the
     fields does not change). For a saved item (itemId) it also says whether it will be moved on save and offers "Copy to
     another project" (src/features/projects.ts copyItem; the copy is saved at once, the dialog stays on the original).
     Same markup as the legacy renderProjectPicker (projects.js) it replaced.
     v-model: the chosen project id (the dialog checks it again on save with validProject). -->
<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useDataStore } from '../../stores/data'
import { COPY_KINDS, copyItem, type CopyKind } from '../../features/projects'
import { safeColor } from '../../utils/color'
import { saveAppData } from '../../features/storage/save'

const props = defineProps<{ kind: CopyKind; modelValue: string; itemId?: string | null }>()
const emit = defineEmits<{ 'update:modelValue': [id: string] }>()
const { t } = useI18n()
const data = useProfileData()
const store = useDataStore()

const projects = computed(() => data.value.projects)
/** The saved item (move and copy are offered only for it) */
const src = computed(() => {
  if (!props.itemId) return null
  const list = data.value[COPY_KINDS[props.kind][0]] as { id: string; projectId?: string }[]
  return list.find(x => x.id === props.itemId) || null
})
const copyOpen = ref(false)
const copyTargets = computed(() => projects.value.filter(p => p.id !== src.value?.projectId))
const doneMsg = ref('')
let doneTimer = 0

function copyTo(pid: string) {
  const d = store.getData(), p = d.projects.find(x => x.id === pid)
  if (!p || !props.itemId || !copyItem(d, props.kind, props.itemId, pid)) return
  saveAppData(d)
  copyOpen.value = false
  doneMsg.value = t('Copied to "{name}"', { name: p.name })
  clearTimeout(doneTimer)
  doneTimer = window.setTimeout(() => { doneMsg.value = '' }, 4000)
}
onBeforeUnmount(() => clearTimeout(doneTimer))
</script>

<template>
  <div :id="`projPickWrap_${kind}`" :class="{ hidden: projects.length < 2 }">
    <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('PROJECT') }}</label>
    <div :id="`projPick_${kind}`">
      <div class="flex flex-wrap gap-2">
        <button v-for="p in projects" :key="p.id" type="button"
          :class="['inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-xs transition', modelValue === p.id ? 'bg-brand text-white' : 'bg-darkinput text-gray-400 hover:text-gray-200']"
          @click="emit('update:modelValue', p.id)"><span class="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" :style="{ background: safeColor(p.color) }"></span>{{ p.name }}</button>
      </div>
      <template v-if="src">
        <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-gray-500">
          <span v-if="src.projectId !== modelValue" class="text-brand font-semibold"><i class="fas fa-arrow-right-arrow-left mr-1"></i>{{ t('It will be moved when you save') }}</span>
          <span v-else>{{ t('Choose another project to move it.') }}</span>
          <button type="button" class="font-bold text-brand hover:underline" @click="copyOpen = !copyOpen"><i class="fas fa-copy mr-1"></i>{{ t('Copy to another project') }}</button>
        </div>
        <div v-if="copyOpen" class="mt-2 flex flex-wrap gap-2">
          <button v-for="p in copyTargets" :key="p.id" type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-gray-600 hover:border-brand text-xs font-bold text-gray-300"
            @click="copyTo(p.id)"><span class="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" :style="{ background: safeColor(p.color) }"></span>{{ t('Copy to {name}', { name: p.name }) }}</button>
        </div>
        <p v-if="doneMsg" class="mt-2 text-[11px] text-green-400 font-semibold"><i class="fas fa-check mr-1"></i>{{ doneMsg }}</p>
      </template>
    </div>
  </div>
</template>
